import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Rect, Stop } from 'react-native-svg';
import { Text } from './Texte';
import { ShieldCheck } from './icons';
import { elapsedStage } from './GenerationProgress';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { t } from '../i18n';
import { colors, radius, spacing } from '../theme/tokens';

export interface OptimisationEnCoursProps {
  testID?: string;
}

/**
 * Attente de « Optimise mon CV » : une feuille de CV relue par un faisceau, entourée des cinq natures
 * de modification que l'IA peut proposer.
 *
 * HONNÊTE PAR CONSTRUCTION, comme `GenerationProgress` dont il reprend les paliers de durée : la scène
 * est une illustration, jamais un état d'avancement. Ni pourcentage, ni coche, ni étape nommée — le
 * serveur ne renvoie aucun jalon avant sa réponse complète. Les étiquettes sont le vocabulaire du
 * RÉSULTAT (les mêmes que sur les cartes de modification), pas des étapes franchies. Seule la durée
 * écoulée fait changer la phrase sous le titre.
 *
 * « Réduire les animations » : la scène reste fixe, faisceau retiré, lignes réécrites allumées.
 */

const SCENE_MAX = 380;
const SCENE_MIN = 330;
const FEUILLE_L = 180;
const FEUILLE_H = 276;
const CYCLE_MS = 3400;
/** Course du faisceau : il entre par le haut de la feuille et en sort par le bas. */
const FAISCEAU_H = 70;
const FAISCEAU_DE = -FAISCEAU_H;
const FAISCEAU_A = FEUILLE_H;
const BORD_MAX = FAISCEAU_H + FEUILLE_H;

/** Lignes de la feuille qui s'allument après le passage du faisceau : leur centre, dans la feuille. */
const LIGNES_REECRITES = [106, 138, 202, 234];

type Etiquette = { cle: 'impact' | 'keywords' | 'action' | 'clarity' | 'title'; point: string; place: Record<string, number>; phase: number };

const ETIQUETTES: Etiquette[] = [
  { cle: 'title', point: colors.white, place: { top: 30, right: 18 }, phase: -1 },
  { cle: 'impact', point: colors.cyan, place: { top: 84, left: 14 }, phase: 1 },
  { cle: 'keywords', point: '#8C9BFF', place: { top: 150, right: 8 }, phase: -1 },
  { cle: 'action', point: '#C86FD8', place: { top: 236, left: 8 }, phase: 1 },
  { cle: 'clarity', point: colors.corail, place: { top: 292, right: 26 }, phase: -1 },
];

function Ligne({ largeur, hauteur = 7, couleur = '#E1E6EF' }: { largeur: number | `${number}%`; hauteur?: number; couleur?: string }) {
  return <View style={{ height: hauteur, width: largeur, borderRadius: hauteur / 2, backgroundColor: couleur }} />;
}

export function OptimisationEnCours({ testID = 'optimisation-en-cours' }: OptimisationEnCoursProps) {
  const reduit = useReduceMotion();
  const { height: hauteurEcran } = useWindowDimensions();
  const hauteurScene = Math.round(Math.min(SCENE_MAX, Math.max(SCENE_MIN, hauteurEcran * 0.45)));
  const k = hauteurScene / SCENE_MAX;

  const [palier, setPalier] = useState<'start' | 'mid' | 'long'>('start');
  useEffect(() => {
    const debut = Date.now();
    const id = setInterval(() => setPalier(elapsedStage(Date.now() - debut)), 1000);
    return () => clearInterval(id);
  }, []);

  const cycle = useRef(new Animated.Value(0)).current;
  const flottement = useRef(new Animated.Value(0)).current;
  const respiration = useRef(new Animated.Value(0)).current;
  const glisse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reduit) return;
    const boucles = [
      Animated.loop(Animated.timing(cycle, { toValue: 1, duration: CYCLE_MS, easing: Easing.linear, useNativeDriver: true })),
      Animated.loop(
        Animated.sequence([
          Animated.timing(flottement, { toValue: 1, duration: 1800, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
          Animated.timing(flottement, { toValue: 0, duration: 1800, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        ]),
      ),
      Animated.loop(
        Animated.sequence([
          Animated.timing(respiration, { toValue: 1, duration: 2000, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
          Animated.timing(respiration, { toValue: 0, duration: 2000, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        ]),
      ),
      Animated.loop(Animated.timing(glisse, { toValue: 1, duration: 1900, easing: Easing.bezier(0.5, 0, 0.3, 1), useNativeDriver: true })),
    ];
    boucles.forEach((b) => b.start());
    return () => boucles.forEach((b) => b.stop());
  }, [reduit, cycle, flottement, respiration, glisse]);

  const faisceauY = cycle.interpolate({ inputRange: [0, 1], outputRange: [FAISCEAU_DE, FAISCEAU_A] });
  const faisceauOpacite = cycle.interpolate({ inputRange: [0, 0.08, 0.88, 1], outputRange: [0, 1, 1, 0] });
  const lueurOpacite = respiration.interpolate({ inputRange: [0, 1], outputRange: [0.75, 1] });
  const lueurEchelle = respiration.interpolate({ inputRange: [0, 1], outputRange: [1, 1.06] });
  const barreX = glisse.interpolate({ inputRange: [0, 1], outputRange: [-58, 138] });

  /** Une ligne réécrite s'allume juste après le passage du bord bas du faisceau, puis tout s'éteint à la fin du cycle. */
  const opaciteLigne = (centre: number) => {
    if (reduit) return 0.85;
    const f = Math.min(0.9, centre / BORD_MAX);
    return cycle.interpolate({ inputRange: [0, f, f + 0.05, 0.95, 1], outputRange: [0, 0, 1, 1, 0], extrapolate: 'clamp' });
  };

  return (
    <View testID={testID} style={styles.conteneur}>
      <View
        testID={`${testID}-scene`}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[styles.scene, { height: hauteurScene }]}
      >
        <LinearGradient
          colors={['#0E2A72', colors.night, '#0A1640']}
          start={{ x: 0.45, y: 0 }}
          end={{ x: 0.55, y: 1 }}
          style={StyleSheet.absoluteFill}
        />

        {/* Lueur centrée derrière la feuille : aucune ne touche un coin du cadre. */}
        <Animated.View
          pointerEvents="none"
          style={[styles.lueur, { top: hauteurScene * 0.52 - 150, opacity: reduit ? 0.9 : lueurOpacite, transform: [{ scale: reduit ? 1 : lueurEchelle }] }]}
        >
          <Svg width={300} height={300}>
            <Defs>
              <RadialGradient id={`lueur-${testID}`} cx="50%" cy="50%" r="50%">
                <Stop offset="0" stopColor="#785CFF" stopOpacity={0.55} />
                <Stop offset="0.42" stopColor={colors.cyan} stopOpacity={0.22} />
                <Stop offset="0.7" stopColor={colors.cyan} stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Rect x="0" y="0" width="300" height="300" fill={`url(#lueur-${testID})`} />
          </Svg>
        </Animated.View>

        {/* Feuille. */}
        <View style={[styles.feuille, { top: Math.round((hauteurScene - FEUILLE_H) / 2) - 8 }]}>
          <View style={styles.entete}>
            <LinearGradient colors={['#CFE0FA', '#E3D6FA']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.avatar} />
            <View style={styles.enteteLignes}>
              <Ligne largeur="78%" hauteur={10} couleur="#CBD6EE" />
              <Ligne largeur="54%" />
            </View>
          </View>

          <View style={styles.sectionTitre}><Ligne largeur="34%" couleur="#B7C4E4" /></View>
          <View style={styles.lignes}>
            <Reecrite opacite={opaciteLigne(LIGNES_REECRITES[0])} chaud={false}><Ligne largeur="100%" /></Reecrite>
            <Ligne largeur="90%" />
            <Reecrite opacite={opaciteLigne(LIGNES_REECRITES[1])} chaud={false}><Ligne largeur="72%" /></Reecrite>
          </View>

          <View style={styles.sectionTitre}><Ligne largeur="40%" couleur="#B7C4E4" /></View>
          <View style={styles.lignes}>
            <Ligne largeur="100%" />
            <Reecrite opacite={opaciteLigne(LIGNES_REECRITES[2])} chaud><Ligne largeur="86%" /></Reecrite>
            <Ligne largeur="94%" />
            <Reecrite opacite={opaciteLigne(LIGNES_REECRITES[3])} chaud><Ligne largeur="60%" /></Reecrite>
          </View>

          <View style={styles.sectionTitre}><Ligne largeur="30%" couleur="#B7C4E4" /></View>
          <View style={styles.lignes}>
            <Ligne largeur="96%" />
            <Ligne largeur="70%" />
          </View>

          {reduit ? null : (
            <Animated.View
              pointerEvents="none"
              style={[styles.faisceau, { opacity: faisceauOpacite, transform: [{ translateY: faisceauY }] }]}
            >
              <LinearGradient
                colors={['rgba(140,64,248,0)', 'rgba(120,110,255,0.16)', 'rgba(92,188,229,0.38)']}
                locations={[0, 0.62, 1]}
                style={StyleSheet.absoluteFill}
              />
              <LinearGradient
                colors={[colors.cyan, colors.violetLogo, colors.corail]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.faisceauTrait}
              />
            </Animated.View>
          )}
        </View>

        {/* Les cinq natures de modification : le vocabulaire du résultat, pas un état d'avancement. */}
        {ETIQUETTES.map(({ cle, point, place, phase }) => {
          const decalage = flottement.interpolate({ inputRange: [0, 1], outputRange: [0, -6 * phase] });
          const haut = place.top === undefined ? undefined : Math.round(place.top * k);
          return (
            <Animated.View
              key={cle}
              pointerEvents="none"
              style={[styles.etiquette, { ...place, top: haut, transform: [{ translateY: reduit ? 0 : decalage }] }]}
            >
              <View style={[styles.point, { backgroundColor: point }]} />
              <Text style={styles.etiquetteTexte}>{t(`cvImprove.tags.${cle}`)}</Text>
            </Animated.View>
          );
        })}

        {/* Activité : un trait qui glisse, sans pourcentage ni échéance. */}
        <View style={styles.barre}>
          <Animated.View style={[styles.barreSegment, { transform: [{ translateX: reduit ? 70 : barreX }] }]}>
            <LinearGradient
              colors={[colors.cyan, colors.violetLogo, colors.corail]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={StyleSheet.absoluteFill}
            />
          </Animated.View>
        </View>
      </View>

      <View style={styles.textes}>
        <Text testID={`${testID}-message`} accessibilityRole="header" accessibilityLiveRegion="polite" style={styles.titre}>
          {t('progress.improve.start')}
        </Text>
        <Text testID={`${testID}-statut`} style={styles.statut}>
          {palier === 'start' ? t('cvImprove.subtitle') : t(`progress.improve.${palier}`)}
        </Text>
      </View>

      <View testID={`${testID}-garantie`} style={styles.garantie}>
        <View style={styles.garantieIcone}>
          <ShieldCheck size={20} color={colors.electricDeep} strokeWidth={2.2} />
        </View>
        <Text style={styles.garantieTexte}>{t('cvImprove.revue.aideSuite')}</Text>
      </View>
    </View>
  );
}

/** Ligne de la feuille recouverte d'un dégradé qui s'allume (opacité animée ou fixe). */
function Reecrite({ opacite, chaud, children }: { opacite: Animated.AnimatedInterpolation<number> | number; chaud: boolean; children: React.ReactNode }) {
  return (
    <View>
      {children}
      <Animated.View pointerEvents="none" style={[styles.reecrite, { opacity: opacite }]}>
        <LinearGradient
          colors={chaud ? [colors.violetLogo, colors.corail] : [colors.cyan, colors.violetLogo]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  conteneur: { gap: spacing.lg },
  scene: {
    borderRadius: 32,
    overflow: 'hidden',
    shadowColor: colors.night,
    shadowOpacity: 0.3,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 16 },
    elevation: 10,
  },
  lueur: { position: 'absolute', alignSelf: 'center', width: 300, height: 300 },
  feuille: {
    position: 'absolute',
    alignSelf: 'center',
    width: FEUILLE_L,
    height: FEUILLE_H,
    borderRadius: 20,
    backgroundColor: colors.white,
    overflow: 'hidden',
    paddingVertical: 22,
    paddingHorizontal: 18,
    transform: [{ rotate: '-3deg' }],
    shadowColor: '#030A28',
    shadowOpacity: 0.45,
    shadowRadius: 22,
    shadowOffset: { width: 0, height: 14 },
  },
  entete: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 40, height: 40, borderRadius: 20 },
  enteteLignes: { flex: 1, gap: 7 },
  sectionTitre: { marginTop: 22 },
  lignes: { marginTop: 12, gap: 9 },
  reecrite: { ...StyleSheet.absoluteFillObject, top: -1, bottom: -1, borderRadius: 4, overflow: 'hidden' },
  faisceau: { position: 'absolute', left: 0, right: 0, top: 0, height: FAISCEAU_H },
  faisceauTrait: { position: 'absolute', left: 0, right: 0, bottom: 0, height: 3 },
  etiquette: {
    position: 'absolute',
    height: 30,
    paddingLeft: 9,
    paddingRight: 12,
    borderRadius: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(7, 27, 77, 0.78)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.26)',
  },
  point: { width: 8, height: 8, borderRadius: 4 },
  etiquetteTexte: { fontSize: 12, lineHeight: 16, fontWeight: '700', color: colors.white },
  barre: {
    position: 'absolute',
    bottom: 22,
    alignSelf: 'center',
    width: 132,
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
  barreSegment: { width: 53, height: 4, borderRadius: 2, overflow: 'hidden' },
  textes: { gap: spacing.sm, paddingHorizontal: spacing.xs },
  titre: { fontSize: 30, lineHeight: 34, fontWeight: '800', letterSpacing: -0.9, color: colors.navy },
  statut: { fontSize: 16, lineHeight: 23, color: colors.grey500 },
  garantie: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md + 2,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.white,
    backgroundColor: 'rgba(255, 255, 255, 0.72)',
  },
  garantieIcone: {
    width: 36,
    height: 36,
    borderRadius: radius.md - 4,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  garantieTexte: { flex: 1, fontSize: 14, lineHeight: 20, color: colors.grey700 },
});
